'use client'

import { useNavigate } from 'react-router-dom'
import { LogOut, User, ChevronDown, KeyRound, Menu } from 'lucide-react'
import { toast } from 'sonner'
import { useState, useRef, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { useAuthStore, useUser } from '@/stores/auth-store'
import { useBizLineStore, type BizLine } from '@/stores/biz-line-store'
import { ChangePasswordDialog } from '@/components/common/ChangePasswordDialog'
import CustomSelect from '@/components/common/CustomSelect'

const bizLineOptions: { value: BizLine; label: string }[] = [
  { value: 'affiliate', label: '推广联盟' },
  { value: 'ads', label: '广告投放' },
  { value: 'cas', label: '获客系统' },
]
export default function Header({ onMenuClick }: { onMenuClick?: () => void }) {
  const navigate = useNavigate()
  const user = useUser()
  const { logout } = useAuthStore()
  const [showDropdown, setShowDropdown] = useState(false)
  const [showChangePassword, setShowChangePassword] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()
  const bizLine = useBizLineStore((s) => s.bizLine)
  const setBizLine = useBizLineStore((s) => s.setBizLine)

  // 按登录返回的可切换业务线过滤下拉（历史会话无该字段时放开全部，后端仍逐接口鉴权）
  const allowedBizLines = user?.allowedBizLines
  const visibleBizLineOptions = allowedBizLines?.length
    ? bizLineOptions.filter(option => allowedBizLines.includes(option.value))
    : bizLineOptions

  /**
   * 切换业务线
   * <p>
   * 业务页面 URL 带 /{bizLine} 前缀，只更新 store 不跳转的话，原业务线的页面组件
   * 会继续挂载并用新业务线的 API 前缀发请求（数据错乱），因此切换后统一跳转到
   * 新业务线的欢迎页
   * </p>
   *
   * @param value 新业务线
   */
  const handleBizLineChange = (value: BizLine) => {
    if (value === bizLine) return
    setBizLine(value)
    queryClient.invalidateQueries({ queryKey: ['userMenus'] })
    navigate(`/${value}/welcome`)
  }

  // 持久化的业务线可能已不在当前账号可切换列表内（角色被改/换设备登录），自动纠正到第一个可用业务线
  useEffect(() => {
    if (allowedBizLines?.length && !allowedBizLines.includes(bizLine)) {
      const fallback = allowedBizLines[0] as BizLine
      setBizLine(fallback)
      queryClient.invalidateQueries({ queryKey: ['userMenus'] })
      navigate(`/${fallback}/welcome`)
    }
  }, [allowedBizLines, bizLine, setBizLine, navigate, queryClient])

  const handleLogout = () => {
    logout()
    toast.success('已退出登录')
    navigate('/login')
  }

  const handleChangePasswordSuccess = () => {
    logout()
    navigate('/login')
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <>
      <header className="h-14 bg-white border-b border-neutral-200 px-3 sm:px-6 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3 sm:gap-4">
          {/* 移动端汉堡按钮：唤出侧栏抽屉（桌面侧栏常驻，不显示） */}
          <button
            onClick={onMenuClick}
            className="md:hidden -ml-1 p-2 rounded-md text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
            title="打开菜单"
          >
            <Menu className="h-5 w-5" />
          </button>
          <h1 className="text-base font-semibold text-neutral-900">
            管理后台
          </h1>
        </div>

         <div className="flex items-center gap-2 sm:gap-3">
          <CustomSelect
            value={bizLine}
            onChange={(value) => handleBizLineChange(value as BizLine)}
            options={visibleBizLineOptions}
            className="w-32 sm:w-36"
          />

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center gap-3 px-3 py-1.5 rounded-md hover:bg-neutral-100"
            >
              <div className="h-8 w-8 rounded-md bg-primary-500 flex items-center justify-center">
                {user?.realName ? (
                  <span className="text-sm font-medium text-white">
                    {user.realName.charAt(0).toUpperCase()}
                  </span>
                ) : (
                  <User className="h-4 w-4 text-white" />
                )}
              </div>

              <div className="hidden sm:flex flex-col items-start">
                <span className="text-sm font-medium text-neutral-900 leading-tight">
                  {user?.realName || user?.username || '用户'}
                </span>
                <span className="text-xs text-neutral-500 leading-tight">
                  {user?.roleName || '管理员'}
                </span>
              </div>

              <ChevronDown className={cn(
                'hidden sm:block h-4 w-4 text-neutral-400 transition-transform',
                showDropdown && 'rotate-180'
              )} />
            </button>

            {showDropdown && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-md border border-neutral-200 shadow-lg py-1">
                <div className="px-4 py-3 border-b border-neutral-100">
                  <p className="text-sm font-medium text-neutral-900">
                    {user?.realName || user?.username || '用户'}
                  </p>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    {user?.username || 'admin'}
                  </p>
                </div>

                <div className="py-1">
                  <button
                    onClick={() => {
                      setShowDropdown(false)
                      setShowChangePassword(true)
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
                  >
                    <KeyRound className="h-4 w-4 text-neutral-400" />
                    <span>修改密码</span>
                  </button>

                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-red-600"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>退出登录</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <ChangePasswordDialog
        open={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        onSuccess={handleChangePasswordSuccess}
      />
    </>
  )
}

function cn(...classes: (string | boolean | undefined)[]): string {
  return classes.filter(Boolean).join(' ')
}